import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { alpha, styled } from "@mui/material/styles";
import DeleteIconButton from "_shared/_views/buttons/delete-icon-button";
import type React from "react";

/**
 * Edit, Publish and Unpublish: neutral until pointed at or focused, then blue, the way Delete beside them
 * is neutral until it goes red. Blue at rest made Edit the loudest thing on every message (#5839).
 */
const QuietIconButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.text.secondary,
  "&:hover, &.Mui-focusVisible": {
    backgroundColor: alpha(theme.palette.primary.main, theme.palette.action.hoverOpacity),
    color: theme.palette.primary.main,
  },
}));

type MessageActionButtonProps = {
  /** What pressing it does, as its tooltip and its accessible name: "Edit comment", "Delete message". */
  label: string;
  icon: React.ReactNode;
  disabled?: boolean;
  /** Goes red under the pointer rather than blue, like every other inline delete in the app. */
  destructive?: boolean;
  onClick: () => void;
};

/**
 * One control in a message's info row: Edit, Delete, Publish, Unpublish.
 *
 * Both Correspondence panes draw a message's controls with this, beside its time and its History, so a
 * control is the same size, colour and place on a work agreement message as on a proposal comment. Every
 * one rests gray. The read-state button beside them stays blue, because its colour says something.
 */
export function MessageActionButton(props: Readonly<MessageActionButtonProps>) {
  const { label, icon, disabled, destructive, onClick } = props;
  const Button = destructive ? DeleteIconButton : QuietIconButton;

  return (
    <Tooltip title={label}>
      <span>
        <Button
          aria-label={label}
          size="small"
          disabled={disabled}
          onClick={(event: React.MouseEvent) => {
            // A message that is unread marks itself read when pressed; the control is the only thing pressed.
            event.stopPropagation();
            onClick();
          }}
        >
          {icon}
        </Button>
      </span>
    </Tooltip>
  );
}
